import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';

// Componentes do app
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import FriendlyPhrase from '../../components/FriendlyPhrase/FriendlyPhrase';

// Contextos
import { useLoader } from '../../contexts/LoaderContext';
import { useToast } from '../../contexts/ToastContext';

// Estilos
import styles from './helppage.module.css';

// Constantes
import HELP_CONTENT from '../../constants/helpContent';

// --- Utils ---

function patternToRegex(pattern) {
  const escaped = pattern
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    .replace(/\\:([a-zA-Z0-9_]+)/g, '[^/]+');
  return new RegExp(`^${escaped}$`);
}

function matchAnyPattern(pathname, patterns = []) {
  return patterns.some((p) => patternToRegex(p).test(pathname));
}

function slugify(s) {
  return String(s)
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function highlight(text, term) {
  if (!term) return text;
  const rx = new RegExp(`(${escapeRegExp(term)})`, 'ig');
  return text.replace(rx, '<mark>$1</mark>');
}

// --- Blocos ---

function Callout({ variant = 'info', text }) {
  return (
    <div className={`${styles.callout} ${styles[`callout--${variant}`]}`}>
      <div className={styles.calloutBadge}>
        {variant === 'info' && 'ℹ️'}
        {variant === 'warning' && '⚠️'}
        {variant === 'tip' && '💡'}
      </div>
      <div className={styles.calloutText}>{text}</div>
    </div>
  );
}

function CodeBlock({ code, lang = 'text' }) {
  return (
    <pre className={styles.codeBlock} data-lang={lang}>
      <code>{code}</code>
    </pre>
  );
}

function ListBlock({ style = 'bulleted', items = [] }) {
  if (style === 'numbered') {
    return (
      <ol className={styles.ol}>
        {items.map((it, idx) => (
          <li key={idx} dangerouslySetInnerHTML={{ __html: it }} />
        ))}
      </ol>
    );
  }
  if (style === 'cards') {
    return (
      <div className={styles.cardGrid}>
        {items.map((it, idx) => (
          <div className={styles.card} key={idx}>
            {it.title && <h4 className={styles.cardTitle}>{it.title}</h4>}
            {it.body && <p className={styles.cardBody}>{it.body}</p>}
          </div>
        ))}
      </div>
    );
  }
  return (
    <ul className={styles.ul}>
      {items.map((it, idx) => (
        <li key={idx} dangerouslySetInnerHTML={{ __html: it }} />
      ))}
    </ul>
  );
}

function StepsBlock({ steps = [] }) {
  return (
    <ol className={styles.steps}>
      {steps.map((s, idx) => (
        <li key={idx} className={styles.stepItem}>
          <div className={styles.stepTitle}>{s.title}</div>
          {s.detail && <div className={styles.stepDetail}>{s.detail}</div>}
        </li>
      ))}
    </ol>
  );
}

function QABlock({ items = [] }) {
  return (
    <div className={styles.qa}>
      {items.map((qa, idx) => (
        <details key={idx} className={styles.qaItem}>
          <summary className={styles.qaQ}>Q: {qa.q}</summary>
          <div className={styles.qaA}>A: {qa.a}</div>
        </details>
      ))}
    </div>
  );
}

function BlockRenderer({ block }) {
  if (!block) return null;
  switch (block.type) {
    case 'text': return <p className={styles.paragraph}>{block.text}</p>;
    case 'list': return <ListBlock style={block.style} items={block.items} />;
    case 'callout': return <Callout variant={block.variant} text={block.text} />;
    case 'steps': return <StepsBlock steps={block.steps} />;
    case 'code': return <CodeBlock code={block.code} lang={block.lang} />;
    case 'qa': return <QABlock items={block.items} />;
    default: return null;
  }
}

function CollapsibleSection({ section, defaultOpen = false, forceOpen = undefined }) {
  const anchorId = section.id || slugify(section.title);
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    if (typeof forceOpen === 'boolean') setOpen(forceOpen);
  }, [forceOpen]);

  return (
    <section id={anchorId} className={styles.section}>
      <button
        className={`${styles.sectionHeader} ${open ? styles.open : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`${anchorId}-content`}
      >
        <span className={styles.chevron} aria-hidden>▸</span>
        <h3 className={styles.sectionTitle}>{section.title}</h3>
      </button>
      <div
        id={`${anchorId}-content`}
        className={`${styles.sectionBody} ${open ? styles.sectionBodyOpen : ''}`}
      >
        {section.blocks?.map((b, i) => (
          <BlockRenderer key={i} block={b} />
        ))}
      </div>
    </section>
  );
}

// --- Hub responsivo (sidebar à direita e drawer no mobile) ---

export default function HelpPage() {
  const location = useLocation();
  const { showLoader, hideLoader } = useLoader();
  const { showToast } = useToast();

  const pages = HELP_CONTENT.pages || [];

  const initialPageId = useMemo(() => {
    const path = location.pathname;
    const matched = pages.find((p) => matchAnyPattern(path, p.routeMatch || []));
    return matched?.id || pages[0]?.id;
  }, [location.pathname, pages]);

  const [selectedId, setSelectedId] = useState(initialPageId);
  const selectedPage = useMemo(
    () => pages.find((p) => p.id === selectedId) || pages[0],
    [pages, selectedId]
  );

  // Busca
  const [q, setQ] = useState('');
  const norm = useCallback((s) => (s || '').toString().toLowerCase(), []);
  const hasQ = q.trim().length > 0;

  const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const highlight = (text, term) => {
    if (!term) return text;
    const rx = new RegExp(`(${escapeRegExp(term)})`, 'ig');
    return text.replace(rx, '<mark>$1</mark>');
  };

  const searchResults = useMemo(() => {
    if (!hasQ) return [];
    const term = norm(q.trim());
    const results = [];

    pages.forEach((p) => {
      const pTitle = norm(p.title);
      const pSummary = norm(p.summary);
      const pAudience = (p.audience || []).map(norm).join(' ');
      const pageMatched = pTitle.includes(term) || pSummary.includes(term) || pAudience.includes(term);

      (p.sections || []).forEach((sec) => {
        const sTitle = norm(sec.title);
        let matched = sTitle.includes(term);

        (sec.blocks || []).forEach((b) => {
          if (matched) return;
          switch (b.type) {
            case 'text':
            case 'callout':
              matched = norm(b.text).includes(term);
              break;
            case 'list':
              if (Array.isArray(b.items)) {
                matched = b.items.some((it) => norm(it.title || it.body || it).includes(term));
              }
              break;
            case 'steps':
              if (Array.isArray(b.steps)) {
                matched = b.steps.some(
                  (st) => norm(st.title).includes(term) || norm(st.detail).includes(term)
                );
              }
              break;
            case 'code':
              matched = norm(b.code).includes(term);
              break;
            case 'qa':
              if (Array.isArray(b.items)) {
                matched = b.items.some(
                  (qa) => norm(qa.q).includes(term) || norm(qa.a).includes(term)
                );
              }
              break;
            default:
              break;
          }
        });

        if (matched || pageMatched) {
          results.push({
            pageId: p.id,
            pageTitle: p.title,
            sectionId: sec.id || slugify(sec.title),
            sectionTitle: sec.title,
            snippet: sec.title
          });
        }
      });
    });

    const key = (r) => `${r.pageId}__${r.sectionId}`;
    const map = new Map();
    results.forEach((r) => { if (!map.has(key(r))) map.set(key(r), r); });
    return Array.from(map.values());
  }, [hasQ, norm, pages, q]);

  const openSectionsSet = useMemo(() => {
    if (!hasQ) return new Set();
    return new Set(
      searchResults
        .filter((r) => r.pageId === selectedId)
        .map((r) => r.sectionId)
    );
  }, [hasQ, searchResults, selectedId]);

  const sidebarPages = useMemo(() => {
    if (!hasQ) return pages;
    const matchedIds = new Set(searchResults.map((r) => r.pageId));
    return [
      ...pages.filter((p) => matchedIds.has(p.id)),
      ...pages.filter((p) => !matchedIds.has(p.id)),
    ];
  }, [hasQ, pages, searchResults]);

  const handleResultClick = (res) => {
    setSelectedId(res.pageId);
    setIsSidebarOpen(false);
    setTimeout(() => {
      const el = document.getElementById(res.sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  // Loader
  useEffect(() => {
    showLoader('Carregando ajuda…');
    const t = setTimeout(() => hideLoader(), 100);
    return () => clearTimeout(t);
  }, [selectedId]);

  useEffect(() => {
    if (!selectedPage) showToast('Conteúdo de ajuda não encontrado.', 3000, 'error');
  }, [selectedPage]);

  // --- Drawer responsivo ---
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const sidebarRef = useRef(null);
  const overlayRef = useRef(null);

  // Trava o scroll do body quando o drawer está aberto (mobile)
  useEffect(() => {
    const body = document.body;
    if (isSidebarOpen) {
      body.style.overflow = 'hidden';
    } else {
      body.style.overflow = '';
    }
    return () => { body.style.overflow = ''; };
  }, [isSidebarOpen]);

  // Fecha com ESC e clique no overlay
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setIsSidebarOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) setIsSidebarOpen(false);
  };

  return (
    <div className={styles.HelpPage}>
        <div className={styles.containerTop}>
            <Breadcrumbs />
            <div className={styles.headerRow}>
                <h1 className={styles.title}>Central de Ajuda</h1>
                <button
                className={styles.mobileToggle}
                onClick={() => setIsSidebarOpen(true)}
                aria-haspopup="dialog"
                aria-controls="help-sidebar"
                aria-expanded={isSidebarOpen}
                >
                Buscar
                </button>
            </div>

            <div className={styles.metaRow}>
                <span className={styles.badge}>{HELP_CONTENT.product}</span>
                <span className={styles.meta}>
                v{HELP_CONTENT.version} • Atualizado em{' '}
                {new Date(HELP_CONTENT.updatedAt).toLocaleDateString()}
                </span>
            </div>

            {/* Subtítulo opcional mostrado no print */}
            <p className={styles.helperIntro}>
                Escolha uma das opções abaixo para continuar.
            </p>

            <FriendlyPhrase />
        </div>

      <div className={styles.containerList}>
        <div className={styles.layout}>
          {/* MAIN (esquerda) */}
          <main className={styles.main}>
            {selectedPage && (
              <>
                <header className={styles.pageHeader}>
                  <h2
                    className={styles.pageTitle}
                    dangerouslySetInnerHTML={{ __html: hasQ ? highlight(selectedPage.title, q) : selectedPage.title }}
                  />
                  {selectedPage.summary && (
                    <p
                      className={styles.summary}
                      dangerouslySetInnerHTML={{ __html: hasQ ? highlight(selectedPage.summary, q) : selectedPage.summary }}
                    />
                  )}

                  <div className={styles.metaInline}>
                    {selectedPage.audience?.map((role) => (
                      <span className={styles.audienceChip} key={role}>{role}</span>
                    ))}
                  </div>

                  {selectedPage.quickLinks?.length > 0 && (
                    <div className={styles.quickLinks}>
                      {selectedPage.quickLinks.map((ql) => (
                        <a key={ql.anchor} href={ql.anchor} className={styles.quickLink}>
                          {ql.label}
                        </a>
                      ))}
                    </div>
                  )}
                </header>

                <div className={styles.content}>
                  {selectedPage.sections?.map((sec, idx) => {
                    const secId = sec.id || slugify(sec.title);
                    const shouldForceOpen = openSectionsSet.has(secId);
                    return (
                      <CollapsibleSection
                        key={secId}
                        section={sec}
                        defaultOpen={idx === 0 && !hasQ}
                        forceOpen={hasQ ? shouldForceOpen : undefined}
                      />
                    );
                  })}

                  <div className={styles.footerNote}>
                    <span>
                      Precisa de mais ajuda? Veja também{' '}
                      <Link to="/testes">Testes</Link>.
                    </span>
                  </div>
                </div>
              </>
            )}
          </main>

          {/* SIDEBAR (direita). Em mobile aparece como drawer */}
          <aside
            id="help-sidebar"
            className={`${styles.sidebar} ${isSidebarOpen ? styles.sidebarOpen : ''}`}
            aria-label="Navegação da ajuda"
            ref={sidebarRef}
          >
            <div className={styles.sidebarHeader}>
              <input
                type="search"
                placeholder="Buscar em toda a ajuda…"
                className={styles.search}
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              {hasQ && (
                <div className={styles.searchMeta}>
                  {searchResults.length} resultado{searchResults.length === 1 ? '' : 's'}
                </div>
              )}
            </div>

            {hasQ ? (
              <div className={styles.results}>
                {searchResults.map((r, i) => (
                  <button
                    key={`${r.pageId}_${r.sectionId}_${i}`}
                    className={styles.resultItem}
                    onClick={() => handleResultClick(r)}
                    title={`${r.pageTitle} • ${r.sectionTitle}`}
                  >
                    <div
                      className={styles.resultTitle}
                      dangerouslySetInnerHTML={{
                        __html: highlight(`${r.pageTitle} — ${r.sectionTitle}`, q),
                      }}
                    />
                    <div
                      className={styles.resultSnippet}
                      dangerouslySetInnerHTML={{ __html: highlight(r.snippet, q) }}
                    />
                  </button>
                ))}
                {searchResults.length === 0 && (
                  <div className={styles.noResults}>Nada encontrado.</div>
                )}
              </div>
            ) : (
              <nav className={styles.pageList}>
                {sidebarPages.map((p) => (
                  <button
                    key={p.id}
                    className={`${styles.pageItem} ${p.id === selectedId ? styles.pageItemActive : ''}`}
                    onClick={() => { setSelectedId(p.id); setIsSidebarOpen(false); }}
                    aria-current={p.id === selectedId ? 'page' : undefined}
                  >
                    <div
                      className={styles.pageItemTitle}
                      dangerouslySetInnerHTML={{
                        __html: hasQ ? highlight(p.title, q) : p.title
                      }}
                    />
                    {p.audience?.length > 0 && (
                      <div className={styles.pageItemAudience}>
                        {p.audience.map((role) => (
                          <span className={styles.audienceChip} key={role}>{role}</span>
                        ))}
                      </div>
                    )}
                    {p.summary && (
                      <div
                        className={styles.pageItemSummary}
                        dangerouslySetInnerHTML={{
                          __html: hasQ ? highlight(p.summary, q) : p.summary
                        }}
                      />
                    )}
                  </button>
                ))}
              </nav>
            )}

            {/* Botão fechar visível apenas no mobile (CSS) */}
            <button
              className={styles.closeDrawer}
              onClick={() => setIsSidebarOpen(false)}
              aria-label="Fechar busca"
            >
              Fechar
            </button>
          </aside>

          {/* Overlay do drawer (só mobile) */}
          <div
            ref={overlayRef}
            className={`${styles.overlay} ${isSidebarOpen ? styles.overlayVisible : ''}`}
            onClick={handleOverlayClick}
            aria-hidden={!isSidebarOpen}
          />
        </div>
      </div>
    </div>
  );
}